import { CloudUploadOutlined } from "@mui/icons-material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { Button } from "@mui/material";
import { useState } from "react";
import { toast } from "sonner";

import type { DocumentListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";
import { DocumentRow } from "./DocumentRow";
import { UploadDocumentModal } from "./UploadDocumentModal";

interface DocumentsSectionProps {
  title: string;
  documents: DocumentListItemDto[] | undefined;
  isLoading: boolean;
  onUpload: (file: File, displayName?: string) => Promise<unknown>;
  isUploading: boolean;
  onDelete: (documentID: string) => Promise<unknown>;
  isEditable?: boolean;
}

export const DocumentsSection = ({
  title,
  documents,
  isLoading,
  onUpload,
  isUploading,
  onDelete,
  isEditable = true,
}: DocumentsSectionProps): React.ReactNode => {
  const { t } = useResource();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingID, setDeletingID] = useState<string | null>(null);

  const handleDelete = async (documentID: string) => {
    setDeletingID(documentID);
    try {
      await onDelete(documentID);
    } catch {
      toast.error(t(Resources.Documents.DeleteError));
    } finally {
      setDeletingID(null);
    }
  };

  const items: BlDetailSectionItem[] = (documents ?? []).map((doc) => ({
    id: String(doc.documentID),
    startAdornment: (
      <DetailSection.IconAdornment color="inherit">
        <ArticleOutlinedIcon />
      </DetailSection.IconAdornment>
    ),
    label: {
      text: doc.displayName ?? doc.fileName,
      title: doc.displayName ?? doc.fileName,
    },
    endAdornment: (
      <DocumentRow
        documentID={String(doc.documentID)}
        fileName={doc.fileName}
        fileID={doc.fileID}
        onDelete={handleDelete}
        isDeleting={deletingID === String(doc.documentID)}
      />
    ),
  }));

  return (
    <>
      <DetailSection
        title={title}
        action={
          isEditable ? (
            <Button
              variant="text"
              color="primary"
              startIcon={<CloudUploadOutlined />}
              onClick={() => setIsModalOpen(true)}
              sx={{ textTransform: "none" }}
            >
              {t(Resources.Documents.UploadButton)}
            </Button>
          ) : undefined
        }
        items={items}
        isLoading={isLoading}
        emptyMessage={t(Resources.Documents.NoDocuments)}
      />

      <UploadDocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={onUpload}
        isLoading={isUploading}
      />
    </>
  );
};
